import { cacheLife } from "next/cache"
import { cache } from "react"
import {
  buildWooRatingFilter,
  getProductBrandValues,
  getProductPrice,
  getProductRating,
  getProductStoreTokens,
  isBadRequest,
  normalizeFilterToken,
  parseTotalHeader,
  toPositiveNumberOrUndefined,
} from "@/utilities/woo-commerce-helpers"
import { wooCommerceApi } from "./api"

interface ProductImage {
  alt: string
  date_created: string
  date_created_gmt: string
  date_modified: string
  date_modified_gmt: string
  id: number
  name: string
  sizes: string
  src: string
  srcset: string
  thumbnail: string
}

export interface ProductCategory {
  id: number
  name: string
  slug: string
}

export interface ProductAttribute {
  id: number
  name: string
  options: string[]
  position: number
  slug: string
  variation: boolean
  visible: boolean
}

interface ProductDimensions {
  height: string
  length: string
  width: string
}

interface StoreAddress {
  city: string
  country: string
  state: string
  street_1: string
  street_2: string
  zip: string
}

interface Store {
  address: StoreAddress
  id: number
  name: string
  shop_name: string
  url: string
}

interface ProductMetaData {
  id: number
  key: string
  value: string | string[]
}

interface ProductLinks {
  collection: Array<{
    href: string
  }>
  self: Array<{
    href: string
    targetHints?: {
      allow: string[]
    }
  }>
}

export interface WooCommerceProduct {
  _links: ProductLinks
  acf: unknown[]
  attributes: ProductAttribute[]
  average_rating: string
  backordered: boolean
  backorders: "no" | "notify" | "yes"
  backorders_allowed: boolean
  brands: unknown[]
  button_text: string
  catalog_visibility: "visible" | "catalog" | "search" | "hidden"
  categories: ProductCategory[]
  cross_sell_ids: number[]
  date_created: string
  date_created_gmt: string
  date_modified: string
  date_modified_gmt: string
  date_on_sale_from: string | null
  date_on_sale_from_gmt: string | null
  date_on_sale_to: string | null
  date_on_sale_to_gmt: string | null
  default_attributes: unknown[]
  description: string
  dimensions: ProductDimensions
  download_expiry: number
  download_limit: number
  downloadable: boolean
  downloads: unknown[]
  external_url: string
  featured: boolean
  global_unique_id: string
  grouped_products: unknown[]
  has_options: boolean
  id: number
  images: ProductImage[]
  low_stock_amount: number | null
  manage_stock: boolean
  menu_order: number
  meta_data: ProductMetaData[]
  name: string
  on_sale: boolean
  parent_id: number
  permalink: string
  post_password: string
  price: string
  price_html: string
  purchasable: boolean
  purchase_note: string
  rating_count: number
  regular_price: string
  related_ids: number[]
  reviews_allowed: boolean
  sale_price: string
  shipping_class: string
  shipping_class_id: number
  shipping_required: boolean
  shipping_taxable: boolean
  short_description: string
  sku: string
  slug: string
  sold_individually: boolean | null
  status: "publish" | "draft" | "pending" | "private"
  stock_quantity: number | null
  stock_status: "instock" | "outofstock" | "onbackorder"
  store: Store
  tags: unknown[]
  tax_class: string
  tax_status: "taxable" | "shipping" | "none"
  total_sales: number
  type: "simple" | "grouped" | "external" | "variable"
  upsell_ids: number[]
  variations: unknown[]
  virtual: boolean
  weight: string
}

interface GetProductsOptions {
  attribute?: string
  attribute_term?: string
  brand?: string
  category?: string | number
  max_price?: number
  min_price?: number
  on_sale?: boolean
  order?: "asc" | "desc"
  orderby?: "date" | "id" | "title" | "popularity" | "rating" | "menu_order"
  page?: number
  per_page?: number
  rating?: string
  search?: string
  status?: "publish" | "draft" | "pending"
  stock_status?: "instock" | "outofstock" | "onbackorder"
}

export interface WooCommerceReview {
  date_created: string
  id: number
  product_id: number
  rating: number
  review: string
  reviewer: string
  reviewer_email?: string
  reviewer_avatar_urls: {
    24: string
    48: string
    96: string
  }
  status?: string
  verified: boolean
  product_permalink?: string
  // Customer Reviews plugin stores attachments in comment meta
  meta_data?: Array<{
    key: string
    value: string | number | boolean | object
  }>
}

interface FetchProductsByCategoryOptions {
  categoryId: number
  excludeProductId: number
  limit: number
  revalidate?: number
}

export const getProducts = cache(
  async ({
    page = 1,
    per_page = 10,
    search = "",
    category = "",
    status = "publish",
    orderby = "date",
    order = "desc",
    min_price,
    max_price,
    on_sale,
    stock_status,
    brand,
    attribute,
    attribute_term,
    rating,
  }: GetProductsOptions = {}): Promise<WooCommerceProduct[]> => {
    "use cache"

    cacheLife("hours")

    const { data: products } = await wooCommerceApi.get<WooCommerceProduct[]>(
      "/products",
      {
        params: {
          page,
          per_page,
          status,
          orderby,
          order,
          ...(search && { search }),
          ...(category && { category }),
          ...(min_price !== undefined && { min_price }),
          ...(max_price !== undefined && { max_price }),
          ...(on_sale !== undefined && { on_sale }),
          ...(stock_status && { stock_status }),
          ...(brand && { brand }),
          ...(attribute && { attribute }),
          ...(attribute_term && { attribute_term }),
          ...(rating && { rating }),
        },
      }
    )

    return products
  }
)

export const getCategoryBySlug = cache(
  async (slug: string): Promise<ProductCategory | null> => {
    "use cache"

    cacheLife("hours")

    const { data: categories } = await wooCommerceApi.get<ProductCategory[]>(
      "/products/categories",
      {
        params: {
          slug,
        },
      }
    )

    return categories[0] ?? null
  }
)

type CategoryProductSortBy =
  | "menu_order"
  | "date"
  | "id"
  | "title"
  | "popularity"
  | "rating"
type CategoryProductSortOrder = "asc" | "desc"

interface GetCategoryProductsPageOptions {
  brand?: string
  discount?: boolean
  max_price?: number
  min_price?: number
  min_rating?: number
  on_sale?: boolean
  order?: CategoryProductSortOrder
  orderby?: CategoryProductSortBy
  page?: number
  per_page?: number
  q?: string
  slug: string
  store_ids?: string[]
}

export interface CategoryProductsPageResult {
  category: ProductCategory | null
  products: WooCommerceProduct[]
  totalPages: number
  totalProducts: number
}

export interface CategoryFilterStoreOption {
  id: string
  label: string
}

export interface CategoryFilterBrandOption {
  id: string
  label: string
}

export interface CategoryFilterRatingOption {
  count: number
  label: string
  value: number
}

export interface CategoryAvailableFiltersResult {
  brands: CategoryFilterBrandOption[]
  priceRange: {
    min: number
    max: number
  }
  ratings: CategoryFilterRatingOption[]
  stores: CategoryFilterStoreOption[]
}

interface FetchCategoryProductsInput {
  brand?: string
  brandMode: "taxonomy" | "attribute" | "none"
  categoryId: number
  discount?: boolean
  maxPrice?: number
  minPrice?: number
  minRating?: number
  order: CategoryProductSortOrder
  orderby: CategoryProductSortBy
  page: number
  perPage: number
  q?: string
  ratingMode: "server" | "none"
}

const fetchCategoryProductsPage = async ({
  categoryId,
  page,
  perPage,
  q,
  minPrice,
  maxPrice,
  discount,
  orderby,
  order,
  brand,
  brandMode,
  minRating,
  ratingMode,
}: FetchCategoryProductsInput): Promise<{
  products: WooCommerceProduct[]
  totalPages: number
  totalProducts: number
}> => {
  const { data: products, headers } = await wooCommerceApi.get<
    WooCommerceProduct[]
  >("/products", {
    params: {
      category: categoryId,
      page,
      per_page: perPage,
      status: "publish",
      stock_status: "instock",
      orderby,
      order,
      ...(q && { search: q }),
      ...(minPrice !== undefined && { min_price: minPrice }),
      ...(maxPrice !== undefined && { max_price: maxPrice }),
      ...(discount && { on_sale: true }),
      ...(brand && brandMode === "taxonomy" && { brand }),
      ...(brand &&
        brandMode === "attribute" && {
          attribute: "pa_brand",
          attribute_term: brand,
        }),
      ...(minRating &&
        ratingMode === "server" && { rating: buildWooRatingFilter(minRating) }),
    },
  })

  return {
    products,
    totalPages: parseTotalHeader(headers["x-wp-totalpages"]),
    totalProducts: parseTotalHeader(headers["x-wp-total"]),
  }
}

interface CategoryProductsQuery {
  brand?: string
  discount: boolean
  maxPrice?: number
  minPrice?: number
  minRating?: number
  page: number
  perPage: number
  q?: string
  storeIds: string[]
}

const buildCategoryQuery = ({
  page,
  per_page,
  q,
  store_ids,
  brand,
  min_rating,
  discount,
  min_price,
  max_price,
  on_sale,
}: Omit<
  GetCategoryProductsPageOptions,
  "slug" | "orderby" | "order"
>): CategoryProductsQuery => ({
  page: Math.max(1, page ?? 1),
  perPage: Math.max(1, per_page ?? 12),
  q: q?.trim() || undefined,
  brand: brand?.trim() || undefined,
  minRating: toPositiveNumberOrUndefined(min_rating),
  minPrice: toPositiveNumberOrUndefined(min_price),
  maxPrice: toPositiveNumberOrUndefined(max_price),
  discount: Boolean(discount || on_sale),
  storeIds: (store_ids ?? [])
    .map((storeId) => storeId.trim())
    .filter((storeId) => storeId.length > 0),
})

interface CategoryPageFetchResult {
  products: WooCommerceProduct[]
  totalPages: number
  totalProducts: number
}

const fetchCategoryPage = async ({
  categoryId,
  query,
  orderby,
  order,
}: {
  categoryId: number
  query: CategoryProductsQuery
  orderby: CategoryProductSortBy
  order: CategoryProductSortOrder
}): Promise<CategoryPageFetchResult> => {
  const brandMode = query.brand ? "taxonomy" : "none"
  const ratingMode = query.minRating ? "server" : "none"

  try {
    const pageResult = await fetchCategoryProductsPage({
      categoryId,
      page: query.page,
      perPage: query.perPage,
      q: query.q,
      minPrice: query.minPrice,
      maxPrice: query.maxPrice,
      discount: query.discount,
      orderby,
      order,
      brand: query.brand,
      brandMode,
      minRating: query.minRating,
      ratingMode,
    })

    return pageResult
  } catch (error) {
    if (!isBadRequest(error)) {
      throw error
    }

    return {
      products: [],
      totalPages: 0,
      totalProducts: 0,
    }
  }
}

export const getCategoryProductsPage = cache(
  async ({
    slug,
    page = 1,
    per_page = 12,
    q,
    store_ids = [],
    brand,
    min_rating,
    discount,
    min_price,
    max_price,
    on_sale,
    orderby = "menu_order",
    order = "asc",
  }: GetCategoryProductsPageOptions): Promise<CategoryProductsPageResult> => {
    "use cache"

    cacheLife("minutes")

    const category = await getCategoryBySlug(slug)

    if (!category) {
      return {
        category: null,
        products: [],
        totalPages: 0,
        totalProducts: 0,
      }
    }

    const query = buildCategoryQuery({
      page,
      per_page,
      q,
      store_ids,
      brand,
      min_rating,
      discount,
      min_price,
      max_price,
      on_sale,
    })

    const pageResult = await fetchCategoryPage({
      categoryId: category.id,
      query,
      orderby,
      order,
    })

    const needsClientFiltering =
      query.storeIds.length > 0 || query.minRating !== undefined

    if (!needsClientFiltering) {
      return {
        category,
        products: pageResult.products,
        totalPages: pageResult.totalPages,
        totalProducts: pageResult.totalProducts,
      }
    }

    const normalizedStoreTokenSet =
      query.storeIds.length > 0
        ? new Set(
            query.storeIds.map((storeId) => normalizeFilterToken(storeId))
          )
        : null

    const filteredProducts = pageResult.products.filter((product) => {
      if (normalizedStoreTokenSet) {
        const productStoreTokens = getProductStoreTokens(product)
        let matchesStore = false

        for (const storeToken of normalizedStoreTokenSet) {
          if (productStoreTokens.has(storeToken)) {
            matchesStore = true
            break
          }
        }

        if (!matchesStore) {
          return false
        }
      }

      if (query.minRating !== undefined) {
        return getProductRating(product) >= query.minRating
      }

      return true
    })

    return {
      category,
      products: filteredProducts,
      totalPages: pageResult.totalPages,
      totalProducts: pageResult.totalProducts,
    }
  }
)

export const getCategoryAvailableFilters = cache(
  async (slug: string): Promise<CategoryAvailableFiltersResult> => {
    "use cache"

    cacheLife("hours")

    const category = await getCategoryBySlug(slug)

    if (!category) {
      return {
        stores: [],
        brands: [],
        ratings: [],
        priceRange: { min: 0, max: 0 },
      }
    }

    const filtersPage = 1
    const filtersPerPage = 100
    const pageProducts = await getProducts({
      page: filtersPage,
      per_page: filtersPerPage,
      category: category.id,
      status: "publish",
      stock_status: "instock",
      orderby: "menu_order",
      order: "asc",
    })

    const storeMap = new Map<string, CategoryFilterStoreOption>()
    const brandMap = new Map<string, CategoryFilterBrandOption>()
    let minPrice = Number.POSITIVE_INFINITY
    let maxPrice = 0

    for (const product of pageProducts) {
      const productPrice = getProductPrice(product)
      if (productPrice > 0) {
        minPrice = Math.min(minPrice, productPrice)
        maxPrice = Math.max(maxPrice, productPrice)
      }

      const store = product.store
      if (store?.id) {
        const storeId = String(store.id)
        const label = store.shop_name || store.name || `Loja #${store.id}`
        storeMap.set(storeId, { id: storeId, label })
      }

      for (const brandValue of getProductBrandValues(product)) {
        const brandId = normalizeFilterToken(brandValue)
        if (brandId.length === 0 || brandMap.has(brandId)) {
          continue
        }

        brandMap.set(brandId, {
          id: brandId,
          label: brandValue,
        })
      }
    }

    const ratings: CategoryFilterRatingOption[] = []

    for (let rating = 5; rating >= 1; rating -= 1) {
      let count = 0

      for (const product of pageProducts) {
        if (getProductRating(product) >= rating) {
          count += 1
        }
      }

      ratings.push({
        value: rating,
        label: `${rating}+ estrelas`,
        count,
      })
    }

    return {
      stores: [...storeMap.values()].sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR")
      ),
      brands: [...brandMap.values()].sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR")
      ),
      ratings,
      priceRange: {
        min: Number.isFinite(minPrice) ? Math.floor(minPrice) : 0,
        max: Math.ceil(maxPrice),
      },
    }
  }
)

export const getProductBySlug = cache(
  async (slug: string): Promise<WooCommerceProduct | null> => {
    "use cache"

    cacheLife("hours")

    const { data: product } = await wooCommerceApi.get<WooCommerceProduct[]>(
      "/products",
      {
        params: {
          slug,
        },
      }
    )

    return product[0] || null
  }
)

export async function getProductsByIds(
  ids: number[]
): Promise<WooCommerceProduct[]> {
  "use cache"

  cacheLife("hours")

  if (ids.length === 0) {
    return []
  }

  const { data: products } = await wooCommerceApi.get<WooCommerceProduct[]>(
    "/products",
    {
      params: {
        include: ids.join(","),
      },
    }
  )

  return products
}

export async function getProductsByCategory(
  options: FetchProductsByCategoryOptions
): Promise<WooCommerceProduct[]> {
  "use cache"

  cacheLife("hours")

  const { categoryId, excludeProductId, limit } = options

  const { data: products } = await wooCommerceApi.get<WooCommerceProduct[]>(
    "/products",
    {
      params: {
        category: categoryId,
        exclude: excludeProductId,
        per_page: limit,
      },
    }
  )

  return products
}

async function fetchProductReviews(
  productId: number,
  options: {
    perPage?: number
    page?: number
    status?: string
    reviewer_email?: string
  } = {}
): Promise<WooCommerceReview[]> {
  const perPage = Math.max(1, options.perPage ?? 100)
  const page = Math.max(1, options.page ?? 1)

  const params: Record<string, string | number> = {
    product: productId,
    per_page: perPage,
    page,
    // Request comment meta to be included in the response
    _fields:
      "id,date_created,product_id,rating,review,reviewer,reviewer_avatar_urls,status,verified,meta_data",
  }

  if (options.status) {
    params.status = options.status
  }

  if (options.reviewer_email) {
    params.reviewer_email = options.reviewer_email
  }

  const { data: reviews } = await wooCommerceApi.get<WooCommerceReview[]>(
    "/products/reviews",
    { params }
  )

  return reviews
}

export async function getProductReviews(
  productId: number,
  options: {
    perPage?: number
    page?: number
  } = {}
): Promise<WooCommerceReview[]> {
  "use cache"

  cacheLife("minutes")

  return await fetchProductReviews(productId, options)
}

export function getFreshProductReviews(
  productId: number,
  options: {
    perPage?: number
    page?: number
  } = {}
): Promise<WooCommerceReview[]> {
  return fetchProductReviews(productId, options)
}

export function getUserPendingReviews(
  productId: number,
  reviewerEmail: string
): Promise<WooCommerceReview[]> {
  return fetchProductReviews(productId, {
    status: "hold",
    reviewer_email: reviewerEmail,
    perPage: 10,
  })
}

interface WooCommerceCustomer {
  billing?: {
    first_name?: string
    last_name?: string
    email?: string
    phone?: string
    cpf?: string
  }
  email: string
  first_name: string
  id: number
  last_name: string
  meta_data?: Array<{
    key: string
    value: unknown
  }>
  shipping?: {
    first_name?: string
    last_name?: string
    phone?: string
  }
}

export const getWooCommerceCustomer = async (
  email: string
): Promise<{ id: number; name: string; email: string } | null> => {
  try {
    const { data: customers } = await wooCommerceApi.get<WooCommerceCustomer[]>(
      "/customers",
      { params: { email, context: "edit" } }
    )

    const customer = customers[0]
    if (!customer) return null

    const name =
      [customer.first_name, customer.last_name].filter(Boolean).join(" ") ||
      email.split("@")[0] ||
      "Cliente"

    return { id: customer.id, name, email: customer.email }
  } catch {
    return null
  }
}

export interface CustomerCpfData {
  billing_cpf: string | null
  billing_phone: string | null
  shipping_cpf: string | null
  shipping_phone: string | null
}

/**
 * Fetches billing and shipping CPF and phone from WooCommerce customer endpoint.
 * WordPress's custom addresses endpoint doesn't return billing_cpf, shipping_cpf,
 * billing_phone, or shipping_phone in responses.
 *
 * WooCommerce stores:
 * - CPF fields in meta_data (billing_cpf, shipping_cpf)
 * - Phone fields directly in billing/shipping objects (billing.phone, shipping.phone)
 */
export const getCustomerCpfData = async (
  userId: number | string
): Promise<CustomerCpfData> => {
  try {
    const { data } = await wooCommerceApi.get<WooCommerceCustomer>(
      `/customers/${userId}`
    )

    const result: CustomerCpfData = {
      billing_cpf: data.billing?.cpf?.trim() || null,
      shipping_cpf: null,
      billing_phone: data.billing?.phone?.trim() || null,
      shipping_phone: data.shipping?.phone?.trim() || null,
    }

    // Extract CPF fields from meta_data
    if (data.meta_data) {
      const metaMap = new Map(
        data.meta_data.map((meta) => [meta.key, meta.value])
      )

      const billingCpf = metaMap.get("billing_cpf")
      const shippingCpf = metaMap.get("shipping_cpf")

      if (!result.billing_cpf && typeof billingCpf === "string") {
        result.billing_cpf = billingCpf.trim()
      }
      if (typeof shippingCpf === "string") {
        result.shipping_cpf = shippingCpf.trim()
      }
    }

    return result
  } catch (error) {
    console.error("Failed to fetch customer CPF data:", error)
    return {
      billing_cpf: null,
      shipping_cpf: null,
      billing_phone: null,
      shipping_phone: null,
    }
  }
}

/**
 * Updates customer CPF and phone in WooCommerce.
 * This ensures the data is persisted even if WordPress custom endpoint doesn't return it.
 *
 * CPF is stored in meta_data, phone is stored in billing/shipping objects.
 */
export const updateCustomerCpfAndPhone = async (
  userId: number | string,
  mode: "billing" | "shipping",
  cpf: string,
  phone: string
): Promise<boolean> => {
  try {
    const cpfMetaKey = mode === "billing" ? "billing_cpf" : "shipping_cpf"

    // First, get existing meta_data to preserve other fields
    const { data: customer } = await wooCommerceApi.get<WooCommerceCustomer>(
      `/customers/${userId}`
    )

    const existingMeta = customer.meta_data || []

    // Remove existing CPF meta if present
    const filteredMeta = existingMeta.filter((meta) => meta.key !== cpfMetaKey)

    // Add new CPF meta
    const updatedMeta = [
      ...filteredMeta,
      { key: cpfMetaKey, value: cpf.trim() },
    ]

    // Prepare the update payload
    // CPF goes to meta_data, phone goes to billing/shipping object
    const updatePayload: Record<string, unknown> = {
      meta_data: updatedMeta,
    }

    if (mode === "billing") {
      updatePayload.billing = {
        ...customer.billing,
        phone: phone.trim(),
      }
    } else {
      updatePayload.shipping = {
        ...customer.shipping,
        phone: phone.trim(),
      }
    }

    // Update customer
    await wooCommerceApi.put(`/customers/${userId}`, updatePayload)

    return true
  } catch (error) {
    console.error(`Failed to update customer ${mode} CPF and phone:`, error)
    return false
  }
}
